// The phone-only Add to cart bar (snippets/sticky-buy.liquid). It shows while the page's own Add to cart button is out
// of view, mirrors that button (label, sold out, adding, price) and clicks it, so Dawn's product form does the adding.
// Both stores, every width (Oct 17, design critique): Dawn checks the first size on load, so nothing stopped an S nobody
// chose from going in the cart, and sizes can't be exchanged. Until the shopper picks a size, the size row shows nothing
// chosen and both Add to cart buttons read "Choose a size" and take them to the row instead. A link that names a
// variant (an ad, Google Shopping, a size picked before a reload) counts as chosen, unless it says pick=size: our own
// links that only carry a color (Wear it on) add that.
if (!customElements.get('sticky-buy')) {
  customElements.define(
    'sticky-buy',
    class StickyBuy extends HTMLElement {
      connectedCallback() {
        // every listener goes when the bar does (Wear it on swaps the whole product in place, bar included)
        this.off = new AbortController();
        const signal = this.off.signal;
        this.sectionId = this.dataset.section;
        this.button = this.querySelector('button');
        this.hasSize = this.dataset.size === 'true';
        this.chosen = this.dataset.ready === 'true' && new URLSearchParams(location.search).get('pick') !== 'size';
        this.phone = window.matchMedia('(max-width: 749px)');
        this.calm = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.root = document.getElementById(`MainProduct-${this.sectionId}`) ?? document;

        // A size pick ends the wait (a click counts even on the size Dawn already checked: no change event fires then);
        // on products without sizes, any pick does
        const picked = (event) => {
          const input = event.target.closest?.(event.type === 'click' ? 'input[type="radio"]' : 'input[type="radio"], select');
          if (!input || !input.closest(`#MainProduct-${this.sectionId} variant-selects`)) return;
          if (this.hasSize && !this.isSize(input.closest('fieldset, .product-form__input'))) return;
          this.chosen = true;
          this.apply();
          this.later();
        };
        document.addEventListener('click', picked, { signal });
        document.addEventListener('change', picked, { signal });
        // The page's own button asks for the size first, the same as the bar's
        document.addEventListener('click', (event) => {
          if (this.chosen || !this.hasSize || !this.main()?.contains(event.target)) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.ask();
        }, { capture: true, signal });

        this.button.addEventListener('click', () => this.act());
        let queued = false;
        this.queue = () => {
          if (queued) return;
          queued = true;
          requestAnimationFrame(() => {
            queued = false;
            this.apply();
            this.update();
          });
        };
        window.addEventListener('scroll', this.queue, { passive: true, signal });
        window.addEventListener('resize', this.queue, { signal });
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () => this.later());
        }
        // Dawn re-renders the picker and relabels its button after every pick, and marks it while adding: follow both
        // (the waiting look goes back on before the next paint, so the re-rendered size never flashes as chosen)
        this.watch = new MutationObserver(() => { this.apply(); this.queue(); });
        this.watch.observe(this.root, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'aria-disabled', 'disabled'] });
        this.apply();
        this.update();
      }

      disconnectedCallback() {
        this.off?.abort();
        this.watch?.disconnect();
        this.unsubscribe?.();
        clearTimeout(this.askTimer);
      }

      // Dawn swaps the button and price after a variant change; read them again once it has
      later() {
        setTimeout(() => this.queue(), 60);
      }

      // Only the button in this bar's own product: Dawn's swap (Wear it on) keeps the old page hidden for 0.5 s with its
      // ids renamed, and a lookup by id from there relabelled the new page's button (a one-size trucker read "Choose a
      // size" after a tee, Oct 19)
      main() {
        const id = `ProductSubmitButton-${this.sectionId}`;
        return this.root === document ? document.getElementById(id) : this.root.querySelector(`#${CSS.escape(id)}`);
      }

      isSize(group) {
        const name = group?.querySelector('legend, label')?.textContent ?? '';
        return /\bsize\b/i.test(name);
      }

      sizeGroup() {
        return [...this.root.querySelectorAll('variant-selects fieldset, variant-selects .product-form__input')].find((g) => this.isSize(g));
      }

      // The waiting look: the size row shows nothing chosen, the page's button asks for a size
      apply() {
        const waiting = this.hasSize && !this.chosen;
        const group = this.sizeGroup();
        if (group && group.hasAttribute('data-pick-pending') !== waiting) group.toggleAttribute('data-pick-pending', waiting);
        const main = this.main(), span = main?.querySelector('span');
        if (!span || main.disabled) return;
        if (waiting && span.textContent.trim() !== this.button.dataset.choose) {
          this.addLabel = span.textContent.trim();
          span.textContent = this.button.dataset.choose;
        } else if (!waiting && span.textContent.trim() === this.button.dataset.choose) {
          span.textContent = this.addLabel ?? window.variantStrings?.addToCart ?? 'Add to cart';
        }
      }

      update() {
        const main = this.main();
        if (!main) return;
        const box = main.getBoundingClientRect();
        const away = box.bottom < 0 || box.top > window.innerHeight;
        // not over a family set's own builder (Oct 21): it sits above the page's button and has its own Add button
        const set = this.closest('section, .shopify-section')?.querySelector('family-set') ?? document.querySelector('family-set'), sb = set?.getBoundingClientRect();
        const inSet = sb && sb.bottom > 0 && sb.top < window.innerHeight;
        const show = away && !inSet && this.phone.matches;
        this.classList.toggle('is-shown', show);
        this.setAttribute('aria-hidden', String(!show));
        this.button.tabIndex = show ? 0 : -1;

        const busy = main.classList.contains('loading');
        const label = busy ? 'Adding…' : main.querySelector('span')?.textContent.trim() ?? '';
        this.button.disabled = main.disabled;
        this.button.toggleAttribute('aria-busy', busy);
        const text = main.disabled || this.chosen || busy ? label : this.button.dataset.choose;
        if (this.button.textContent !== text) this.button.textContent = text;

        const prices = document.getElementById(`price-${this.sectionId}`);
        const onSale = prices?.querySelector('.price--on-sale');
        const price = onSale
          ? prices.querySelector('.price__sale .price-item--sale')
          : [...(prices?.querySelectorAll('.price__regular .price-item--regular') ?? [])].pop();
        if (price) this.querySelector('[data-sticky-price]').textContent = price.textContent.trim();
      }

      // Take the shopper to the row the button named, and mark it for a moment
      ask() {
        const group = (this.hasSize && this.sizeGroup()) || this.root.querySelector('variant-selects');
        if (!group) return;
        group.scrollIntoView({ behavior: this.calm.matches ? 'auto' : 'smooth', block: 'center' });
        group.querySelector('input:checked, input, select')?.focus({ preventScroll: true });
        group.classList.remove('is-asked');
        void group.offsetWidth;
        group.classList.add('is-asked');
        clearTimeout(this.askTimer);
        this.askTimer = setTimeout(() => group.classList.remove('is-asked'), 1600);
      }

      act() {
        const main = this.main();
        if (!main || main.disabled || main.classList.contains('loading')) return;
        if (!this.chosen) return this.ask();
        main.click();
      }
    }
  );
}
